import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { Button, Stack } from "@mui/material";
import FormikControl from "../../components/formik/FormikControl";
import * as Yup from "yup";

import Loader from "../../components/loader/Loader";
import {
  EditStatusModel,
  GetProjectStatusModel,
} from "../../models/ProjectStatusModel";
import ProjectStatusService from "../../services/ProjectStatusService";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { getProjectStatusAdmin } from "../../redux/actions/ProjectStatusActions";

const validationSchema = Yup.object({
  buApproved: Yup.string().notRequired(),
  sbuApproved: Yup.string().notRequired(),
  sbgApproved: Yup.string().notRequired(),
  csgoApproved: Yup.string().notRequired(),
  rejected: Yup.string().notRequired(),
});
type FormProps = Yup.InferType<typeof validationSchema>;

type Props = {
  handleModalClose: () => void;
  data?: GetProjectStatusModel;
  isBuApprovalRequired?: boolean;
};
const ProjectStatusForm = ({
  handleModalClose,
  data,
  isBuApprovalRequired,
}: Props) => {
  const dispatch = useAppDispatch();
  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const initialValues = useMemo(
    () => ({
      id: 0,
      buApproved: "",
      sbuApproved: "",
      sbgApproved: "",
      csgoApproved: "",
      rejected: "",
    }),
    []
  );
  const [formState, setFormState] = useState<FormProps>(initialValues);

  useEffect(() => {
    if (data) {
      setFormState(() => ({
        id: data.projectId,
        buApproved:
          data.isBuapproved === null
            ? "Not Selected"
            : data.isBuapproved
            ? "Yes"
            : "No",
        sbuApproved:
          data.isSbuapproved === null
            ? "Not Selected"
            : data.isSbuapproved
            ? "Yes"
            : "No",
        sbgApproved:
          data.isSbgapproved === null
            ? "Not Selected"
            : data.isSbgapproved
            ? "Yes"
            : "No",
        csgoApproved:
          data.isCsgoapproved === null
            ? "Not Selected"
            : data.isCsgoapproved
            ? "Yes"
            : "No",
        rejected:
          data.isRejected === null
            ? "Not Selected"
            : data.isRejected
            ? "Yes"
            : "No",
      }));
    }
  }, [data]);

  const submitHandler = async (
    values: FormProps,
    actions: FormikHelpers<FormProps>
  ) => {
    try {
      let formData: EditStatusModel = {
        id: data?.projectId as number,
        buApproved:
          values.buApproved === "Not Selected"
            ? null
            : values.buApproved === "Yes"
            ? true
            : false,
        sbuApproved:
          values.sbuApproved === "Not Selected"
            ? null
            : values.sbuApproved === "Yes"
            ? true
            : false,
        sbgApproved:
          values.sbgApproved === "Not Selected"
            ? null
            : values.sbgApproved === "Yes"
            ? true
            : false,
        csgoApproved:
          values.csgoApproved === "Not Selected"
            ? null
            : values.csgoApproved === "Yes"
            ? true
            : false,
        rejected:
          values.rejected === "Not Selected"
            ? null
            : values.rejected === "Yes"
            ? true
            : false,
      };
      const response = await ProjectStatusService.postProjectStatusAdmin(
        formData
      );
      console.log(response);
      dispatch(getProjectStatusAdmin());
      handleModalClose();
      displayToast(`Project Status updated successfully`, "success");
    } catch (error) {
      actions.setSubmitting(false);
    }
  };

  type Option = {
    value: string;
    label: string;
  };
  const approvalOptions: Option[] = [
    {
      value: "Yes",
      label: "Yes",
    },
    {
      value: "No",
      label: "No",
    },
    {
      value: "Not Selected",
      label: "Not Selected",
    },
  ];

  return (
    <Formik
      initialValues={formState}
      onSubmit={submitHandler}
      enableReinitialize={true}
      validationSchema={validationSchema}
    >
      {(formik) => {
        return (
          <Form>
            <Stack gap={2}>
              <FormikControl
                control="input"
                type="number"
                name="id"
                size="small"
                label="Project Id"
                placeholder="Project Id"
                disabled={true}
              />

              {isBuApprovalRequired && (
                <FormikControl
                  control="autocomplete"
                  options={approvalOptions}
                  name="buApproved"
                  size="small"
                  label="BU Approved"
                  placeholder="Select BU Approval Status"
                  getOptionDisabled={(option: Option) =>
                    option.value === "Yes" || option.value === "No"
                  }
                  disabled={
                    isBuApprovalRequired &&
                    formik.values?.buApproved === "Not Selected"
                  }
                />
              )}
              <FormikControl
                control="autocomplete"
                options={approvalOptions}
                name="sbuApproved"
                size="small"
                label="SBU Approved"
                placeholder="Select SBU Approval Status"
                getOptionDisabled={(option: Option) =>
                  option.value === "Yes" || option.value === "No"
                }
                disabled={
                  (isBuApprovalRequired &&
                    formik.values?.buApproved === "Not Selected") ||
                  formik.values?.sbuApproved === "Not Selected"
                }
              />
              <FormikControl
                control="autocomplete"
                options={approvalOptions}
                name="sbgApproved"
                size="small"
                label="SBG Approved"
                placeholder="Select SBG Approval Status"
                getOptionDisabled={(option: Option) =>
                  option.value === "Yes" || option.value === "No"
                }
                disabled={
                  (isBuApprovalRequired &&
                    formik.values?.buApproved === "Not Selected" &&
                    formik.values?.sbuApproved === "Not Selected") ||
                  formik.values?.sbgApproved === "Not Selected"
                }
              />
              <FormikControl
                control="autocomplete"
                options={approvalOptions}
                name="csgoApproved"
                size="small"
                label="CSGO Approved"
                placeholder="Select CSGO Approval Status"
                getOptionDisabled={(option: Option) =>
                  option.value === "Yes" || option.value === "No"
                }
                disabled={
                  (isBuApprovalRequired &&
                    formik.values?.buApproved === "Not Selected" &&
                    formik.values?.sbuApproved === "Not Selected" &&
                    formik.values?.sbgApproved === "Not Selected") ||
                  formik.values?.csgoApproved === "Not Selected"
                }
              />
              <FormikControl
                control="autocomplete"
                options={approvalOptions}
                name="rejected"
                size="small"
                label="Reject"
                placeholder="Select Rejection Status"
                getOptionDisabled={(option: Option) =>
                  option.value === "Yes" || option.value === "No"
                }
                disabled={formik.values?.rejected === "Not Selected"}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={formik.isSubmitting}
              >
                Submit
              </Button>
            </Stack>
            {formik.isSubmitting && <Loader />}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ProjectStatusForm;
